import { Heading, Spinner } from "@flixbus/honeycomb-react";
import {
  HorizontalNavigation,
  DefaultInfoAlert,
  Footer,
  internalLocales,
  getUserPreferredLanguage,
} from "@flixbus-phx/marketplace-common";
import * as React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as css from "./Faq.scss";
import getCategories from "./helpers/getCategories/getCategories";
import highlightText from "./helpers/highlightText/highlightText";
import searchArticles from "./helpers/searchArticles/searchArticles";
import useFaqArticles from "./hooks/useFaqArticles/useFaqArticles";
import { Article } from "./types";
import ArticleInfoBox from "./ui/articleInfoBox/ArticleInfoBox";
import NoArticlesFoundMessage from "./ui/noArticlesFoundMessage/NoArticlesFoundMessage";
import SearchInput from "./ui/searchInput/SearchInput";
import SearchResultCount from "./ui/searchResultCount/SearchResultCount";

export const ALL_CATEGORIES = "All";

const Faq: React.FC = () => {
  const { formatMessage } = useIntl();

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchedValue = searchParams.get("q") ?? "";

  const { articles, loading, error } = useFaqArticles();

  const [searchValue, setSearchValue] = React.useState<string>(searchedValue);
  const [searchResult, setSearchResult] = React.useState<Array<Article>>();
  const [tabDisplayed, setTabDisplayed] = React.useState<string>(ALL_CATEGORIES);

  const articlesFilteredByCategory =
    tabDisplayed === ALL_CATEGORIES ? articles : articles.filter((article) => article.category === tabDisplayed);

  const articlesFilteredBySearchValue =
    searchResult !== undefined
      ? searchResult
      : searchedValue && articles.length > 0
        ? searchArticles(searchedValue, articles)
        : undefined;

  const categories = getCategories(articles);
  const articlesToDisplay = articlesFilteredBySearchValue ?? articlesFilteredByCategory;

  if (loading) {
    return (
      <div className={css.spinner}>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return <DefaultInfoAlert message={formatMessage({ id: "general.error.message" })} />;
  }

  return (
    <div className={css.container}>
      <div className={css.headerContainer}>
        <div className={css.innerHeaderContainer}>
          <Heading size={1}>
            <FormattedMessage id="faq.headline" />
          </Heading>
          <SearchInput
            searchValue={searchValue}
            onSearchValueChanged={(value) => {
              setSearchValue(value);
              if (!value) {
                setSearchResult(undefined);
                setSearchParams({}, { replace: true });
              }
            }}
            articles={articles}
            onSearchResultChanged={(result) => {
              setSearchResult(result);
              if (result !== undefined) {
                setSearchParams({ q: searchValue }, { replace: true });
              }
            }}
          />
        </div>
      </div>

      {articlesFilteredBySearchValue === undefined && (
        <div className={css.nav}>
          <HorizontalNavigation
            displayedTab={tabDisplayed}
            onTabClicked={(clickedTab) => setTabDisplayed(clickedTab)}
            labels={[
              {
                text: formatMessage({ id: "general.all" }),
                key: ALL_CATEGORIES,
              },
              ...categories.map((category) => ({ text: category, key: category })),
            ]}
          />
        </div>
      )}

      <div className={css.scrollableArea}>
        {articlesToDisplay.length ? (
          <div className={css.articlesWrapper}>
            <div className={css.innerArticlesWrapper}>
              {articlesFilteredBySearchValue && articlesFilteredBySearchValue.length > 0 && (
                <SearchResultCount numOfSearchResults={articlesFilteredBySearchValue.length} />
              )}

              {articlesToDisplay.map((article) => (
                <ArticleInfoBox
                  key={article.id}
                  headline={
                    articlesFilteredBySearchValue
                      ? highlightText(article.headline, searchedValue, css.highlight)
                      : article.headline
                  }
                  category={
                    articlesFilteredBySearchValue
                      ? highlightText(article.category, searchedValue, css.highlight)
                      : article.category
                  }
                  onClick={() =>
                    navigate(`/faq/${article.id}`, {
                      state: {
                        id: article.id,
                        headline: article.headline,
                        category: article.category,
                        contentHtml: article.contentHtml,
                        searchQuery: searchedValue,
                        lang: getUserPreferredLanguage(),
                      },
                    })
                  }
                />
              ))}
            </div>
          </div>
        ) : (
          <NoArticlesFoundMessage />
        )}
        <div className={css.footerWrapper}>
          <Footer
            isInternal
            locale={internalLocales.find((l) => getUserPreferredLanguage() === l.locale)?.locale || "en"}
          />
        </div>
      </div>
    </div>
  );
};

export default Faq;
